import React, { useMemo } from "react";
import { AbsoluteFill, interpolate, Easing } from "remotion";

/**
 * PullOut Transition - Custom Remotion Transition Presentation
 * 
 * Implements a professional "pull out" zoom transition effect similar to CapCut's transition style.
 * Creates a smooth, dynamic transition where the exiting sequence zooms in and fades out while
 * the entering sequence zooms out to normal size, creating a revealing effect.
 * 
 * Animation Details:
 * - Exiting Sequence: Zooms IN from 1x to 1.5x scale while fading out (opacity 1→0)
 *   Opacity fades quickly (completes by 50% of transition duration)
 * - Entering Sequence: Zooms OUT from 1.5x to 1x scale, appearing beneath the exiting content
 * 
 * Rendering:
 * - Uses z-index layering: exiting content (zIndex: 10) appears on top of entering content (zIndex: 0)
 * - Exiting content is rendered as a regular div with absolute positioning to prevent layout shifts
 * - Both sequences animate simultaneously during the 30-frame (1 second at 30fps) transition
 * 
 * Easing Functions:
 * - Entering zoom: Easing.out(Easing.cubic) - smooth deceleration as content zooms out
 * - Exiting zoom: Easing.in(Easing.cubic) - smooth acceleration as content zooms in
 * - Opacity fade: Easing.in(Easing.cubic) - smooth acceleration of fade-out
 * 
 * @param {Object} [props] - Configuration object (optional)
 * @returns {{component: React.ComponentType, props: Object}} Remotion TransitionPresentation object
 *          containing the PullOutPresentation component and configuration props
 * 
 * @example
 * // Basic usage in TransitionSeries
 * <TransitionSeries.Transition
 *   presentation={pullOut()}
 *   timing={linearTiming({ durationInFrames: 30 })}
 * />
 */
export const pullOut = (props = {}) => {
  return {
    component: PullOutPresentation,
    props,
  };
};

/**
 * PullOutPresentation Component
 * Receives presentationProgress (0-1) and renders the transition effect
 * 
 * @param {Object} componentProps
 * @param {React.ReactNode} componentProps.children - Content to transition
 * @param {number} componentProps.presentationProgress - Progress from 0 to 1
 * @param {string} componentProps.presentationDirection - "entering" or "exiting"
 * @param {Object} componentProps.passedProps - Custom props passed from pullOut()
 */
const PullOutPresentation = ({
  children,
  presentationProgress,
  presentationDirection,
  passedProps,
}) => {
  const isEntering = presentationDirection === "entering";

  const style = useMemo(() => {
    const enteringScale = interpolate(presentationProgress, [0, 1], [1.5, 1], {
      easing: Easing.out(Easing.cubic),
    });

    const exitingScale = interpolate(presentationProgress, [0, 1], [1, 1.5], {
      easing: Easing.in(Easing.cubic),
    });

    const enteringOpacity = interpolate(
      presentationProgress,
      [0, 0.4, 1],
      [0, 0.6, 1],
      {
        easing: Easing.out(Easing.cubic),
      }
    );

    const exitingOpacity = interpolate(
      presentationProgress,
      [0, 0.6, 1],
      [1, 0.6, 0],
      {
        easing: Easing.in(Easing.cubic),
      }
    );

    const scale = isEntering ? enteringScale : exitingScale;
    const opacity = isEntering ? enteringOpacity : exitingOpacity;

    return {
      transform: `scale3d(${scale}, ${scale}, 1)`,
      transformOrigin: "center center",
      opacity,
    };
  }, [isEntering, presentationProgress]);

  return <AbsoluteFill style={style}>{children}</AbsoluteFill>;
};

export default pullOut;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
